import React, { MouseEventHandler } from "react";

import { ButtonStyled, ButtonWithIconStyled } from "./styled";
import { cn } from "utils/helpers";
import { Spinner } from "@chakra-ui/react";

// Type definitions
type ButtonType =
	| string
	| "base"
	| "icon"
	| "primary"
	| "secondary"
	| "plain"
	| "plain-border"
	| "plain-color"
	| "plain-icon"
	| "plain-error";
type SecondaryType = "default" | "white" | boolean;
type ButtonSize = "xs" | "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	active?: boolean;
	alt?: boolean;
	btntype?: ButtonType;
	children: React.ReactNode;
	className?: string;
	disabled?: boolean;
	disabledStyle?: boolean;
	isLoading?: boolean;
	loadingText?: string;
	margin?: string | number;
	onClick?: MouseEventHandler | undefined;
	padding?: string | number;
	secondary?: SecondaryType;
	size?: ButtonSize;
	type?: "button" | "submit" | "reset";
	width?: string | number;
}

interface ButtonWithIconProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	btntype?: ButtonType;
	className?: string;
	height?: string | number;
	icon: React.ReactNode;
	style?: React.CSSProperties;
	text: string;
	width?: string | number;
}

const secondaryClass: Record<string, string> = {
	default: `h-[40px]`,
	white: `h-[40px] !bg-white !text-black !font-semibold px-6 py-4 !border !border-[#D0D5DD] flex gap-2 items-center rounded-[4px] self-center`,
};

export const Button: React.FC<ButtonProps> = ({
	children,
	btntype = "primary",
	alt = false,
	margin,
	padding,
	className,
	disabled,
	active,
	type = "button",
	secondary,
	size = "md",
	isLoading = false,
	loadingText,
	...rest
}) => {
	return (
		<ButtonStyled
			margin={margin}
			btntype={btntype}
			padding={padding}
			type={type}
			disabled={disabled}
			size={size}
			className={cn(
				"disabled:pointer-events-none disabled:border-none disabled:bg-gray-100 disabled:text-gray-400",
				className,
				secondary === true
					? secondaryClass["default"]
					: secondaryClass[secondary as string]
			)}
			active={active}
			alt={alt ? 1 : undefined}
			{...rest}
		>
			{isLoading && <Spinner className="mr-2" />}
			{children}
		</ButtonStyled>
	);
};

export const ButtonWithIcon: React.FC<ButtonWithIconProps> = ({
	text,
	icon,
	style,
	className,
	btntype,
	height,
	width,
	...props
}) => {
	return (
		<ButtonWithIconStyled
			style={{ ...style }}
			className={className}
			btntype={btntype}
			{...props}
		>
			{icon}
			{text}
		</ButtonWithIconStyled>
	);
};
